<script setup lang="ts">
import { ref } from 'vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from 'primevue/usetoast'
import { useUsersStore } from '@/store/modules/user'
import { useAuthStore } from '@/store/modules/auth'
import OTPInput from '@/components/ui/otp-input/OTPInput.vue'
import { useReCaptcha } from 'vue-recaptcha-v3'
import { SocialLogin } from '@capgo/capacitor-social-login'
import { Capacitor } from '@capacitor/core'
import { IconGoogle, IconApple } from '@/components/icons'
import AppModal from '@/components/shared/AppModal.vue'
import { formatError } from '@/utils/errorUtils'

const isNative = Capacitor.isNativePlatform()

const props = defineProps<{
  visible: boolean
  zIndex?: number
}>()
const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
}>()

const usersStore = useUsersStore()
const authStore = useAuthStore()
const toast = useToast()

const loading = ref(false)
const recaptcha = useReCaptcha()
const executeRecaptcha = recaptcha?.executeRecaptcha

const linkEmail = ref('')
const verificationCode = ref('')
const showVerification = ref(false)
const error = ref<string | null>(null)

async function sendLinkCode() {
  if (!linkEmail.value || loading.value) return
  loading.value = true
  error.value = null
  try {
    if (executeRecaptcha) {
      const token = await executeRecaptcha('link_email')
      await authStore.requestCode(linkEmail.value, 'link', token, undefined, usersStore.user?.userId)

      toast.add({ severity: 'success', summary: 'Check your email to complete linking', life: 5000 })
      showVerification.value = true
    }
  } catch (e: any) {
    error.value = formatError(e, 'Failed to send link code')
    toast.add({ severity: 'error', summary: 'Error', detail: error.value, life: 5000 })
  } finally {
    loading.value = false
  }
}

async function handleGoogleLink() {
  loading.value = true
  error.value = null
  let messageHandler: ((event: MessageEvent) => void) | null = null

  try {
    await authStore.clearSocialSessions()
    const googleClientId = (window as any).__APP_CONFIG__?.googleClientId || import.meta.env.VITE_GOOGLE_CLIENT_ID
    
    // Create a promise that resolves when the postMessage is received
    const postMessagePromise = new Promise<{ idToken: string; accessToken: string }>((resolve, reject) => {
      messageHandler = (event: MessageEvent) => {
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
          if (data.provider === 'google') {
            if (data.success && data.result) {
              resolve(data.result)
            } else {
              reject(new Error(data.error || 'Google link failed'))
            }
          }
        } catch (e) {
          // Ignore non-JSON or unrelated messages
        }
      }
      window.addEventListener('message', messageHandler)
    })

    const loginPromise = SocialLogin.login({
      provider: 'google',
      options: { 
        scopes: ['email', 'profile'],
        clientId: googleClientId,
        prompt: 'select_account'
      }
    })

    // Wait for either the plugin to resolve (native) or our postMessage (web)
    const result = await Promise.race([
      loginPromise.then(res => {
        if (res.result && res.result.responseType === 'online') {
          return { idToken: res.result.idToken || '', accessToken: res.result.accessToken?.token || '' }
        }
        throw new Error('Invalid login response')
      }),
      postMessagePromise
    ])

    const idToken = result.idToken
    if (!idToken) throw new Error('No idToken received')
    
    let recaptchaToken: string | undefined
    if (executeRecaptcha) {
      recaptchaToken = await executeRecaptcha('link_google')
    }
    
    await authStore.linkWithGoogle(idToken, recaptchaToken)
    await authStore.checkLogin()
    toast.add({ severity: 'success', summary: 'Google account linked', life: 5000 })
    emit('update:visible', false)
  } catch (err: any) {
    console.error('Link error:', err)
    error.value = formatError(err, 'Linking failed')
    toast.add({ severity: 'error', summary: 'Linking failed', detail: error.value, life: 5000 })
  } finally {
    loading.value = false
    if (messageHandler) {
      window.removeEventListener('message', messageHandler)
    }
  }
}

async function handleAppleLink() {
  loading.value = true
  error.value = null
  let messageHandler: ((event: MessageEvent) => void) | null = null

  try {
    await authStore.clearSocialSessions()
    // Create a promise that resolves when the postMessage is received
    const postMessagePromise = new Promise<{ idToken: string; accessToken: string }>((resolve, reject) => {
      messageHandler = (event: MessageEvent) => {
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
          if (data.provider === 'apple') {
            if (data.success && data.result) {
              resolve(data.result)
            } else {
              reject(new Error(data.error || 'Apple link failed'))
            }
          }
        } catch (e) {
          // Ignore non-JSON or unrelated messages
        }
      }
      window.addEventListener('message', messageHandler)
    })

    const loginPromise = SocialLogin.login({
      provider: 'apple',
      options: { 
        scopes: ['email', 'name'],
      }
    })

    // Wait for either the plugin to resolve (native) or our postMessage (web)
    const result = await Promise.race([
      loginPromise.then(res => {
        if (res.result) {
          return { idToken: res.result.idToken || '', accessToken: '' }
        }
        throw new Error('Invalid login response')
      }),
      postMessagePromise
    ])

    const idToken = result.idToken
    if (!idToken) throw new Error('No idToken received')

    let token: string | undefined
    if (executeRecaptcha) {
      token = await executeRecaptcha('link_apple')
    }

    await authStore.linkWithApple(idToken, token)
    await authStore.checkLogin()
    toast.add({ severity: 'success', summary: 'Apple account linked', life: 5000 })
    emit('update:visible', false)
  } catch (e: any) {
  error.value = formatError(e, 'Linking failed')
  toast.add({ severity: 'error', summary: 'Linking failed', detail: error.value, life: 5000 })
  } finally {
    loading.value = false
    if (messageHandler) {
      window.removeEventListener('message', messageHandler)
    }
  }
}

async function verifyLinkCode(codeToVerify?: string) {
  const codeValue = typeof codeToVerify === 'string' ? codeToVerify : verificationCode.value
  if (loading.value || !codeValue) return
  error.value = null
  try {
    loading.value = true
    if (executeRecaptcha) {
      const token = await executeRecaptcha('verify_code')
      await authStore.verifyCode(linkEmail.value, codeValue, 'link', token)
      await authStore.checkLogin()
      toast.add({ severity: 'success', summary: 'Email successfully linked!', life: 5000 })
      showVerification.value = false
      linkEmail.value = ''
      verificationCode.value = ''
      emit('update:visible', false)
    }
  } catch (e: any) {
    error.value = formatError(e, 'Verification failed')
    toast.add({ severity: 'error', summary: 'Verification failed', detail: error.value, life: 5000 })
    verificationCode.value = ''
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <AppModal
    :visible="props.visible"
    @update:visible="emit('update:visible', $event)"
    title="Link Accounts"
    max-width="max-w-md"
    content-class="bg-white dark:bg-gray-900"
    :z-index="props.zIndex"
  >
    <template v-if="loading">
      <div class="flex flex-col items-center justify-center py-12 space-y-6">
      <div class="h-20 w-20 rounded-full border-4 border-primary/10 border-t-primary animate-spin"></div>
        <div class="text-center space-y-2">
          <h3 class="text-lg font-bold text-gray-900 dark:text-white">Linking Account</h3>
          <p class="text-sm text-gray-500 max-w-[200px]">Please wait while we securely process your request...</p>
        </div>
      </div>
    </template>

    <template v-else-if="!showVerification">
      <div class="space-y-4">
        <label class="block text-sm font-bold text-gray-500 uppercase tracking-widest pl-1">Link with Provider</label>
        <div class="flex flex-col w-full gap-3">
          <Button
            type="button"
            variant="outline"
            class="w-full h-12 rounded-xl border-gray-200 dark:border-gray-800 justify-start px-6 gap-3"
            @click="handleGoogleLink"
          >
            <IconGoogle class="w-5 h-5 flex-shrink-0" />
            <span class="flex-1 text-center pr-5 text-gray-900 dark:text-white">Continue with Google</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            class="w-full h-12 rounded-xl border-gray-200 dark:border-gray-800 justify-start px-6 gap-3"
            @click="handleAppleLink"
          >
            <IconApple class="w-5 h-5 flex-shrink-0" />
            <span class="flex-1 text-center pr-5 text-gray-900 dark:text-white">Continue with Apple</span>
          </Button>
        </div>
      </div>

      <div class="flex items-center gap-2 my-6">
        <div class="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
        <span class="text-xs text-gray-400 font-bold uppercase tracking-widest">or</span>
        <div class="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
      </div>

      <form class="space-y-4" @submit.prevent="sendLinkCode">
        <label class="block text-sm font-bold text-gray-500 uppercase tracking-widest pl-1">Manual Link</label>
        <div class="flex flex-col gap-3">
          <Input v-model="linkEmail" type="email" name="email" inputmode="email" autocomplete="email" autocapitalize="none" spellcheck="false" placeholder="Enter email address" class="w-full h-12 rounded-xl" />
          <Button type="submit" :disabled="!linkEmail" class="w-full h-12 rounded-xl shadow-lg shadow-primary/10">Send Link Code</Button>
        </div>
      </form>

      <!-- Global Error Display -->
      <div v-if="error" class="mt-6 text-center text-sm text-red-500 font-medium p-3 bg-red-50 dark:bg-red-900/10 rounded-xl border border-red-100 dark:border-red-900/20 animate-in fade-in slide-in-from-top-1 whitespace-pre-line">
        {{ error }}
      </div>
    </template>

    <Transition v-else name="fade-slide">
      <div class="space-y-6 py-4">
         <div class="text-center mb-6">
            <p class="text-sm text-gray-500">
              Enter the code sent to <br/><strong>{{ linkEmail }}</strong>
            </p>
         </div>

         <OTPInput v-model="verificationCode" @complete="verifyLinkCode" :disabled="loading" />

         <!-- Verification Error Display -->
         <div v-if="error" class="text-center text-sm text-red-500 font-medium animate-in fade-in slide-in-from-top-1 mt-4 whitespace-pre-line">
            {{ error }}
         </div>
         <div class="flex justify-center mt-6">
           <Button variant="ghost" size="sm" class="text-primary font-bold" @click="showVerification = false">Back to options</Button>
         </div>
      </div>
    </Transition>
  </AppModal>
</template>

<style scoped>
.fade-slide-enter-active, .fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-slide-enter-from { opacity: 0; transform: translateX(20px); }
.fade-slide-leave-to { opacity: 0; transform: translateX(-20px); }
</style>